import { icon as iconDefinition } from '@fortawesome/fontawesome-svg-core/import.macro';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import MuiDrawer from '@mui/material/Drawer';
import { Theme } from '@mui/material/styles/createTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useState, useTransition } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card } from 'src/components/Card';
import { Icon } from 'src/components/Icon';
import { List, ListItem, ListItemProps } from 'src/components/ListItem';
import { Scroller } from 'src/components/Scroller';
import { Stack } from 'src/components/Stack';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { isHQPlatform } from 'src/models/platform/utils';

import { ActionsMenu } from './ActionsMenu';
import { EditNavButton } from './EditNavButton';
import { OnboardingProgress } from './OnboardingProgress';
import { useAdminNav } from './useAdminNav';
import { useMainNav } from './useMainNav';

export interface MainNavProps {
  open: boolean;
  toggleOpen: () => void;
  navWidth?: string;
  isAdmin?: boolean;
}

export const MainNav: React.FC<MainNavProps> = ({
  open,
  toggleOpen,
  navWidth = '280px',
  isAdmin = false,
}) => {
  const { session, isDarkmode } = useOptionalSession();
  const darkMode = isDarkmode();

  const regularLogo = darkMode ? session?.darkmodeLogoUrl : session?.logoUrl;

  const adminLogo = darkMode
    ? 'https://res.cloudinary.com/brandpad/image/upload/c_scale,dpr_auto,f_auto,w_2816/v1/13578/colorprism-textlight-h'
    : 'https://res.cloudinary.com/brandpad/image/upload/c_scale,dpr_auto,f_auto,w_2816/v1/13578/colorprism-textdark-h';

  const logoUrl = isAdmin ? adminLogo : regularLogo;

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const xlDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('xl'));

  return (
    <MuiDrawer
      open={open}
      anchor="left"
      PaperProps={{
        elevation: 0,
        sx: { bgcolor: 'transparent' },
      }}
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: navWidth,
          p: xlDown ? 1 : 0,
        },
        '&& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, .15)',
        },
      }}
      onClose={toggleOpen}
      variant={xlDown ? 'temporary' : 'persistent'}
    >
      <Card sx={{ height: '100%', borderRadius: xlDown ? '12px' : '0px' }}>
        <Stack sx={{ height: '100%' }}>
          <Box
            component="img"
            src={logoUrl}
            sx={{
              maxWidth: '100%',
              maxHeight: '80px',
              height: 'auto',
              width: '100%',
              objectFit: 'contain',
              objectPosition: 'left center',
              px: 2,
              py: 2,
              my: 1,
            }}
          />
          <Scroller
            direction="column"
            hideScrollbar
            sx={{ height: '100%', maxHeight: '100%', overflowY: 'scroll' }}
          >
            {isAdmin ? (
              <AdminNavItems toggleOpen={toggleOpen} />
            ) : (
              <MainNavItems toggleOpen={toggleOpen} />
            )}
          </Scroller>
          {!isAdmin && !isHQPlatform(session) && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <OnboardingProgress />
                </Box>
              </Box>
              {!mdDown && (
                <Box sx={{ px: 2, mb: 2 }}>
                  <EditNavButton />
                </Box>
              )}
            </>
          )}
          {mdDown && (
            <Box sx={{ p: 2, pl: 2 }}>
              <ActionsMenu size="small" />
            </Box>
          )}
        </Stack>
      </Card>
    </MuiDrawer>
  );
};

export interface NavItemProps {
  title: string;
  children?: React.ReactNode;
  route: string;
  reactRoute?: string;
  icon?: ListItemProps['icon'];
  onClick?: () => void;
  target?: string;
  rel?: string;
  level?: number;
}

export const NavItem: React.FC<NavItemProps> = ({
  title,
  icon,
  children,
  route,
  reactRoute,
  onClick,
  target,
  rel,
  level = 0,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Only used when NavItem has children
  const [open, setOpen] = useState(false);
  const [, startTransition] = useTransition();

  const { pathname } = location;

  const navItemStyles = (theme: Theme) => {
    return {
      py: 1.4,
      mb: 0.5,
      ml: level * 1.5,
      color: 'neutral.700', //darkMode ? 'neutral.100' : 'neutral.700',
      ...theme.applyStyles('dark', {
        color: 'neutral.100',
      }),
      '& .MuiListItemIcon-root': {
        color: 'neutral.500',
        ...theme.applyStyles('dark', {
          color: 'neutral.400',
        }),
      },
      '&.Mui-selected': {
        bgcolor: 'primary.main',
        color: 'white',
        '&:hover': {
          bgcolor: 'primary.dark',
        },
        '& .MuiListItemIcon-root': {
          color: 'white',
        },
      },
    };
  };

  let path = reactRoute || (target ? route : '/embed' + route);

  // hack because of bad route data
  if (path.startsWith('/embed/dashboard/fbm/schedule'))
    path = '/embed/fbm/schedule/';
  const rootPath =
    pathname == '/embed/dashboard' &&
    path.startsWith('/embed/dashboard/trainer-dashboard');

  const selected =
    pathname.replace(/\/$/, '') ===
      (reactRoute || (target ? route : '/embed' + route)).replace(/\/$/, '') ||
    rootPath;

  const handleClick = (event) => {
    if (onClick) {
      onClick();
    }
    if (target === '_blank') {
      return;
    }

    if (path) {
      event.preventDefault();
      startTransition(() => {
        navigate(path);
      });
    }
  };

  return (
    <>
      {!children ? (
        <ListItem
          component={target === '_blank' ? 'a' : Link}
          to={target === '_blank' ? undefined : path}
          href={target === '_blank' ? path : undefined}
          clickable
          rounded
          sx={navItemStyles}
          primary={title}
          icon={icon}
          selected={selected}
          onClick={handleClick}
          target={target}
          rel={rel}
          // To force embed page to update when navigating
          // between pages that are both on the embed route
          state={
            target === '_blank'
              ? undefined
              : {
                  renderTime: Date.now(),
                }
          }
        />
      ) : (
        <ListItem
          onClick={() => {
            if (!open) {
              startTransition(() => {
                navigate(path);
              });
            }
            setOpen(!open);
          }}
          clickable
          rounded
          sx={navItemStyles}
          primary={title}
          icon={icon}
          rightAdornment={
            <Box
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
            >
              <Icon
                size="small"
                icon={iconDefinition({ name: 'angle-right', style: 'regular' })}
                sx={{
                  fontSize: '14px',
                  transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  color: 'neutral.400',
                }}
              />
            </Box>
          }
        />
      )}
      <Collapse in={open} collapsedSize={0}>
        <Box
          sx={{
            borderLeft: 1,
            borderColor: 'divider',
            ml: 3.4 + level * 1.5,
            pl: 0,
          }}
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === NavItem) {
              return React.cloneElement(child, {
                ...child.props,
                level: level + 1,
              });
            }
            return child;
          })}
        </Box>
      </Collapse>
    </>
  );
};

const AdminNavItems = ({ toggleOpen }: { toggleOpen: () => void }) => {
  const adminNavItems = useAdminNav();

  const renderNavItems = (items: any[], level = 0) => {
    return items.map((navItem, i) => {
      if (!navItem.enabled) return null;
      return (
        <NavItem
          key={navItem.id + i}
          title={navItem.name}
          icon={level === 0 ? navItem.icon : undefined}
          route={navItem.route}
          reactRoute={navItem.reactRoute}
          onClick={() => !navItem.children?.length && toggleOpen()}
          level={level}
        >
          {!!navItem.children?.length &&
            renderNavItems(navItem.children, level + 1)}
        </NavItem>
      );
    });
  };

  return <List sx={{ mx: 2 }}>{renderNavItems(adminNavItems)}</List>;
};

const MainNavItems = ({ toggleOpen }: { toggleOpen: () => void }) => {
  const mainNavItems = useMainNav();

  const renderNavItems = (items: any[], level = 0) => {
    return items.map((navItem, i) => {
      if (!navItem.enabled) return null;
      return (
        <NavItem
          key={navItem.id + i}
          title={navItem.name}
          icon={level === 0 ? navItem.icon : undefined}
          route={navItem.route}
          reactRoute={navItem.reactRoute}
          onClick={() => !navItem.children?.length && toggleOpen()}
          target={navItem.target}
          rel={navItem.rel}
          level={level}
        >
          {!!navItem.children?.length &&
            renderNavItems(navItem.children, level + 1)}
        </NavItem>
      );
    });
  };

  return <List sx={{ mx: 2 }}>{renderNavItems(mainNavItems)}</List>;
};
